import world from '@/assets/json/world.json';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { MapChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  VisualMapComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { getColor, rgbaColor } from '@/helpers/utils';
import PropTypes from 'prop-types';
import { forwardRef, type CSSProperties } from 'react';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  MapChart,
  CanvasRenderer,
  ToolboxComponent,
  LegendComponent,
  VisualMapComponent
]);

echarts.registerMap('world', { geoJSON: world } as any);

type WorldMapDataItem = {
  name: string;
  value: number;
};

type WorldMapProps = {
  data: WorldMapDataItem[];
  style?: CSSProperties;
  minZoomLevel?: number;
  maxZoomLevel?: number;
};

const getOptions = (
  data: WorldMapDataItem[],
  maxZoomLevel: number,
  minZoomLevel: number
) => ({
  textStyle: {
    fontFamily: 'dana'
  },
  tooltip: {
    trigger: 'item',
    padding: [7, 10],
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-300'),
    textStyle: { color: getColor('gray-1100') },
    borderWidth: 1,
    transitionDuration: 0,
    formatter: (params: {
      data?: { name?: string; value?: number };
      name?: string;
    }) => {
      return `<div><strong>${params?.data?.name || params.name}:</strong> ${
        // ((params.data?.value ?? 0) / total) *
        // 100
        params.data?.value ?? 0
        // .toFixed(2)
      }
    
    </div>`.toPersianDigit();
    }
  },

  visualMap: {
    show: false,
    // Logarithmic buckets keep small but meaningful counts visible when one
    // country has far more backlinks than the rest.
    pieces: getColorPieces(data)
  },
  series: [
    {
      type: 'map',
      map: 'world',
      data,
      roam: true,
      scaleLimit: {
        min: minZoomLevel,
        max: maxZoomLevel
      },
      // Fit the complete GeoJSON bounds inside the responsive chart area.
      layoutCenter: ['50%', '50%'],
      layoutSize: '200%',
      label: {
        show: false
      },
      itemStyle: {
        // Countries without a backlink stay on the theme's neutral map color.
        areaColor: getColor('gray-100'),
        borderColor: getColor('gray-300')
      },
      emphasis: {
        label: {
          show: false
        },
        itemStyle: {
          areaColor: getColor('warning')
        }
      }
    }
  ]
});

const getColorPieces = (data: WorldMapDataItem[]) => {
  const maxValue = Math.max(
    ...data.map(item => Math.max(Number(item.value) || 0, 0)),
    0
  );
  if (!maxValue) return [];

  const colors = [
    rgbaColor(getColor('primary'), 0.25),
    rgbaColor(getColor('primary'), 0.4),
    rgbaColor(getColor('primary'), 0.6),
    rgbaColor(getColor('primary'), 0.8),
    getColor('primary')
  ];
  const logMax = Math.log1p(maxValue);

  return colors
    .map((color, index) => {
      const min =
        index === 0
          ? 0
          : Math.ceil(Math.expm1((index / colors.length) * logMax));
      const max =
        index === colors.length - 1
          ? maxValue
          : Math.ceil(
              Math.expm1(((index + 1) / colors.length) * logMax)
            ) - 1;

      return { min, max, color };
    })
    .filter(piece => piece.min <= piece.max);
};

const WorldMap = forwardRef<ReactEChartsCore, WorldMapProps>(
  ({ data, style, minZoomLevel = 1, maxZoomLevel = 5 }, ref) => {
    return (
      <ReactEChartsCore
        ref={ref}
        echarts={echarts}
        option={getOptions(data, maxZoomLevel, minZoomLevel)}
        style={style}
      />
    );
  }
);

WorldMap.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired
    }).isRequired
  ),
  style: PropTypes.object.isRequired,
  minZoomLevel: PropTypes.number,
  maxZoomLevel: PropTypes.number
};

export default WorldMap;
